import type {
  UseMutationOptions,
  UseMutationResult,
  UseQueryResult,
} from "@tanstack/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import { RefreshClient } from "@citrine/client";
import { queryClient } from "@citrine/client/utils";

import type { GenerateTableConfigRequest } from "../data-query";
import type { IGEMTableConfigMetadata } from "../gem-table";
import type {
  BuildGemTableJobOutput,
  JobExecutionStatus,
} from "../job-execution";
import { JobExecutionClient } from "../job-execution";

import type {
  ITableBuildRequest,
  ITableConfig,
  ITableConfigCreateResponse,
  ITableGenerateConfigResponse,
} from "./table-config";

function getBuildTableProgressQueryKey(
  tableConfigId: string,
  tableConfigVersion: number,
) {
  return ["build-table", tableConfigId, tableConfigVersion, "progress"];
}

export class TableConfigClient extends RefreshClient {
  endpoint: string;
  jobExecutionClient: JobExecutionClient<
    BuildGemTableJobOutput | { default_table_config_from_query: string }
  >;
  protected constructor(
    endpoint: string,
    jobExecutionClient: JobExecutionClient<
      BuildGemTableJobOutput | { default_table_config_from_query: string }
    >,
  ) {
    super();
    this.endpoint = endpoint;
    this.jobExecutionClient = jobExecutionClient;
  }

  static getInstanceForProject(projectId: string) {
    return ProjectTableConfigClient.getInstance(projectId);
  }

  static getInstanceForTeam(teamId: string) {
    return TeamTableConfigClient.getInstance(teamId);
  }

  async build(
    buildUrl: string,
    tableConfigId: string,
    tableConfigVersion: number,
  ): Promise<BuildGemTableJobOutput> {
    const progressQueryKey = getBuildTableProgressQueryKey(
      tableConfigId,
      tableConfigVersion,
    );

    try {
      queryClient.setQueryData(progressQueryKey, true);

      const { job_id } = await this.request<{ job_id: string }>({
        method: "POST",
        url: buildUrl,
      });

      queryClient.setQueryData(progressQueryKey, job_id);

      const { output } = await (this.jobExecutionClient.poll(job_id, {
        interval: 5 * 1000,
        timeout: 60 * 60 * 1000,
      }) as Promise<JobExecutionStatus<BuildGemTableJobOutput>>);

      return output;
    } finally {
      queryClient.removeQueries({ queryKey: progressQueryKey });
    }
  }

  useBuildInProgress(
    tableConfigId: string,
    tableConfigVersion: number,
  ): UseQueryResult<string | boolean> {
    const queryKey = getBuildTableProgressQueryKey(
      tableConfigId,
      tableConfigVersion,
    );
    return useQuery<string | boolean>({
      queryKey,
      queryFn: () =>
        (queryClient.getQueryData(queryKey) as string | boolean | undefined) ??
        false,
    });
  }

  async generateConfigFromQuery(request: GenerateTableConfigRequest) {
    const { job_id } = await this.request<{ job_id: string }>({
      method: "POST",
      url: `${this.endpoint}/from-query/async`,
      params: {
        name: request.query.name,
        algorithm: request.algorithm,
      },
      data: request.query,
    });

    const { output, status } = await (this.jobExecutionClient.poll(job_id, {
      interval: 5 * 1000,
      timeout: 60 * 60 * 1000,
    }) as Promise<
      JobExecutionStatus<{ default_table_config_from_query: string }>
    >);

    if (status === "Failure") {
      // unsure what is returned in a failed task, don't leak potential implementation
      // details to users
      throw new Error("Job failure");
    }

    return JSON.parse(
      output.default_table_config_from_query,
    ) as ITableGenerateConfigResponse;
  }

  useGenerateConfigFromQuery(
    options?: Omit<
      UseMutationOptions<
        ITableGenerateConfigResponse,
        AxiosError,
        GenerateTableConfigRequest
      >,
      "mutationFn"
    >,
  ) {
    return useMutation({
      ...options,
      mutationFn: (request) => this.generateConfigFromQuery(request),
    });
  }
}
class ProjectTableConfigClient extends TableConfigClient {
  constructor(projectId: string) {
    super(
      `/api/v1/projects/${projectId}/table-configs`,
      JobExecutionClient.getInstanceForProject(projectId),
    );
  }

  static getInstance(projectId: string) {
    return new this(projectId);
  }

  useBuild(
    {},
    options: Omit<
      UseMutationOptions<
        BuildGemTableJobOutput,
        AxiosError,
        ITableBuildRequest
      >,
      "mutationFn"
    > = {},
  ): UseMutationResult<BuildGemTableJobOutput, AxiosError, ITableBuildRequest> {
    return useMutation({
      ...options,
      mutationFn: ({ tableConfigId, tableConfigVersion }) => {
        return this.build(
          `${this.endpoint}/${tableConfigId}/versions/${tableConfigVersion}/build`,
          tableConfigId,
          tableConfigVersion,
        );
      },
    });
  }

  async create(definition: ITableConfig): Promise<ITableConfigCreateResponse> {
    const response = await this.request<ITableConfigCreateResponse>({
      method: "POST",
      url: `${this.endpoint}/`,
      data: {
        definition,
      },
    });
    return response;
  }

  useCreate(
    options?: Omit<
      UseMutationOptions<ITableConfigCreateResponse, AxiosError, ITableConfig>,
      "mutationFn"
    >,
  ) {
    return useMutation({
      ...options,
      mutationFn: (definition) => this.create(definition),
    });
  }

  async update(id: string, config: ITableConfig) {
    const response = await this.request<IGEMTableConfigMetadata>({
      method: "PUT",
      url: `${this.endpoint}/${id}`,
      data: {
        table_config: config,
      },
    });
    return response;
  }

  useUpdate(
    id: string,
    options?: Omit<
      UseMutationOptions<IGEMTableConfigMetadata, AxiosError, ITableConfig>,
      "mutationFn"
    >,
  ) {
    return useMutation({
      ...options,
      mutationFn: (config) => this.update(id, config),
    });
  }
}

class TeamTableConfigClient extends TableConfigClient {
  teamId: string;
  constructor(teamId: string) {
    super(
      `/api/v1/teams/${teamId}/table-configs`,
      JobExecutionClient.getInstanceForTeam(teamId),
    );
    this.teamId = teamId;
  }

  static getInstance(teamid: string) {
    return new this(teamid);
  }

  useBuild(
    { projectId }: { projectId: string },
    options: Omit<
      UseMutationOptions<
        BuildGemTableJobOutput,
        AxiosError,
        ITableBuildRequest
      >,
      "mutationFn"
    > = {},
  ): UseMutationResult<BuildGemTableJobOutput, AxiosError, ITableBuildRequest> {
    return useMutation({
      ...options,
      mutationFn: ({ tableConfigId, tableConfigVersion }) => {
        return this.build(
          `/api/v1/teams/${this.teamId}/projects/${projectId}/table-configs/${tableConfigId}/versions/${tableConfigVersion}/build`,
          tableConfigId,
          tableConfigVersion,
        );
      },
    });
  }
}
